import { useState, useEffect } from 'react';
import { VideoRequest, VideoSettings, Provider, CostStats, ImagePreview } from './types/video';
import { apiService } from './services/api';
import { loadPreferences, savePreferences, getDefaultSettings } from './utils/localStorage';
import { useToast } from './hooks/useToast';
import { useIsMobile } from './hooks/useMediaQuery';
import { ToastContainer } from './components/Toast';
import ScenarioInput from './components/ScenarioInput';
import VideoSettingsPanel from './components/VideoSettingsPanel';
import GeneratePanel from './components/GeneratePanel';
import UnifiedOutputPane from './components/UnifiedOutputPane';
import TopBar from './components/TopBar';
import HistoryDrawer from './components/HistoryDrawer';

function App() {
  // Load preferences from localStorage
  const preferences = loadPreferences();

  const [currentVideo, setCurrentVideo] = useState<VideoRequest | null>(null);
  const [videoHistory, setVideoHistory] = useState<VideoRequest[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const { toasts, showToast, removeToast } = useToast();
  const isMobile = useIsMobile();

  // Lifted state for video settings
  const preferredSettings = preferences.settings ?? getDefaultSettings();
  const initialSettings: VideoSettings = {
    ...preferredSettings,
    aspectRatio: preferredSettings.aspectRatio ?? '9:16',
  };

  const [scenarioDescription, setScenarioDescription] = useState('');
  const [settings, setSettings] = useState<VideoSettings>(initialSettings);
  const [quality, setQuality] = useState<'fast' | 'quality'>(preferences.quality || 'fast');
  const [provider, setProvider] = useState<Provider | undefined>(preferences.provider);
  const [costStats, setCostStats] = useState<CostStats | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [queueCount] = useState(0);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [preview, setPreview] = useState<ImagePreview | null>(null);
  const [previewStatus, setPreviewStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'video'>('preview');

  // Save preferences when they change
  useEffect(() => {
    savePreferences({ settings, quality, provider });
  }, [settings, quality, provider]);

  // Load video history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await apiService.getVideoHistory(10);
        if (response.success && response.data) {
          setVideoHistory(response.data);
        }
      } catch (error) {
        console.error('Failed to load video history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    const loadCostStats = async () => {
      try {
        const response = await apiService.getCostStats();
        if (response.success && response.data) {
          setCostStats(response.data);
        }
      } catch (error) {
        console.error('Failed to load cost stats:', error);
      }
    };

    loadHistory();
    loadCostStats();

    const interval = setInterval(loadCostStats, 30000);

    // Load queue status
    const loadQueueStatus = async () => {
      try {
        // Placeholder for queue status - would be replaced with actual API call
        // const response = await apiService.getQueueStatus();
        // setQueueCount(response.count);
      } catch (error) {
        console.error('Failed to load queue status:', error);
      }
    };

    loadQueueStatus();
    const queueInterval = setInterval(loadQueueStatus, 10000);

    return () => {
      clearInterval(interval);
      clearInterval(queueInterval);
    };
  }, []);

  // Handle video generation
  const handleGenerateVideo = async (scenario: string) => {
    const trimmedScenario = scenario.trim();
    if (!trimmedScenario || isGeneratingVideo) {
      return;
    }

    const readyPreview = preview && previewStatus === 'ready' && preview.scenarioDescription === trimmedScenario
      ? preview.imageDataUrl
      : undefined;
    const fallbackPreview = currentVideo?.previewImageData && currentVideo.scenarioDescription === trimmedScenario
      ? currentVideo.previewImageData
      : undefined;

    const previewImageData = readyPreview ?? fallbackPreview;

    if (!previewImageData) {
      showToast('Genereer eerst een image preview voor dit scenario.', 'warning', 4000);
      return;
    }

    setIsGeneratingVideo(true);

    try {
      console.log('Generating video:', { scenarioDescription: trimmedScenario, settings, quality, provider, hasPreview: !!previewImageData });

      const response = await apiService.createVideoRequest({
        scenarioDescription: trimmedScenario,
        settings,
        quality,
        provider,
        previewImageData,
      });

      if (response.success && response.data) {
        const previewReference = previewImageData;
        const enrichedVideo: VideoRequest = {
          ...response.data,
          previewImageData: previewReference,
        };

        setCurrentVideo(enrichedVideo);

        apiService.pollVideoStatus(
          response.data.id,
          (updatedVideo) => {
            const videoWithPreview: VideoRequest = {
              ...updatedVideo,
              previewImageData: previewReference,
            };

            setCurrentVideo(videoWithPreview);

            setVideoHistory(prev => {
              const existsIndex = prev.findIndex(v => v.id === videoWithPreview.id);
              const isTerminal = videoWithPreview.status === 'completed' || videoWithPreview.status === 'failed';

              if (existsIndex !== -1) {
                const next = [...prev];
                next[existsIndex] = videoWithPreview;
                return next;
              }

              if (isTerminal) {
                return [videoWithPreview, ...prev].slice(0, 10);
              }

              return prev;
            });

            if (videoWithPreview.status === 'completed') {
              showToast('Video succesvol gegenereerd!', 'success', 4000);
            } else if (videoWithPreview.status === 'failed') {
              showToast('Video generatie mislukt. Probeer het opnieuw.', 'error', 5000);
            }
          }
        );
      }
    } catch (error: any) {
      console.error('Failed to generate video:', error);
      showToast(`Video generatie mislukt: ${error.message}`, 'error', 5000);
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Handle retry for failed videos
  const handleRetry = () => {
    if (currentVideo?.scenarioDescription) {
      handleGenerateVideo(currentVideo.scenarioDescription);
    }
  };

  const handleGeneratePreview = async () => {
    const trimmedScenario = scenarioDescription.trim();
    if (!trimmedScenario || isGeneratingPreview || isGeneratingVideo) {
      return;
    }

    setIsGeneratingPreview(true);
    setPreviewStatus('loading');
    setPreviewError(null);

    try {
      const response = await apiService.createImagePreview({
        scenarioDescription: trimmedScenario,
        settings,
        quality,
        provider,
      });

      if (response.success && response.data) {
        const previewPayload: ImagePreview = {
          ...response.data,
          scenarioDescription: trimmedScenario,
        };
        setPreview(previewPayload);
        setPreviewStatus('ready');
        showToast('Preview gegenereerd. Controleer deze voordat je de video start.', 'success', 3000);
        return;
      }

      setPreview(null);
      setPreviewStatus('error');
      setPreviewError('Preview kon niet worden opgehaald.');
    } catch (error: any) {
      console.error('Failed to generate preview:', error);
      const message = error?.message || 'Preview generatie mislukt.';
      setPreview(null);
      setPreviewStatus('error');
      setPreviewError(message);
      showToast(message, 'error', 4000);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Handle aspect ratio change
  const handleAspectRatioChange = (newAspectRatio: string) => {
    setSettings(prev => ({ ...prev, aspectRatio: newAspectRatio as '9:16' | '16:9' }));
  };

  const trimmedScenario = scenarioDescription.trim();
  const isCurrentProcessing = currentVideo?.status === 'processing';
  const previewMatchesScenario = preview?.scenarioDescription === trimmedScenario;
  const previewAligned = Boolean(preview && previewMatchesScenario);
  const isPreviewReady = previewStatus === 'ready' && previewAligned;
  const canGeneratePreview = trimmedScenario.length > 0 && !isGeneratingPreview && !isGeneratingVideo;
  const canStartGeneration = trimmedScenario.length > 0 && !isGeneratingVideo && !isCurrentProcessing && isPreviewReady;

  const handleGenerateFromState = () => {
    if (isCurrentProcessing) {
      return;
    }
    handleGenerateVideo(scenarioDescription);
  };

  const previewImageForPane = previewMatchesScenario ? preview?.imageDataUrl : currentVideo?.previewImageData;
  const previewPrompt = previewMatchesScenario ? preview?.promptUsed : undefined;
  const previewStatusForPane: 'idle' | 'loading' | 'ready' | 'error' = previewMatchesScenario
    ? previewStatus
    : (currentVideo?.previewImageData ? 'ready' : previewStatus);
  const previewImageForVideo = currentVideo?.previewImageData || (isPreviewReady ? preview?.imageDataUrl : undefined);
  const previewStatusForPanel: 'idle' | 'loading' | 'ready' | 'error' = previewMatchesScenario
    ? previewStatus
    : (previewStatus === 'loading' ? 'loading' : 'idle');

  useEffect(() => {
    if (!trimmedScenario && previewStatus !== 'loading') {
      setPreview(null);
      setPreviewStatus('idle');
      setPreviewError(null);
    }
  }, [trimmedScenario, previewStatus]);

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <TopBar
        totalCost={costStats?.formattedCost}
        onToggleHistory={() => setIsHistoryOpen(prev => !prev)}
        isHistoryOpen={isHistoryOpen}
        historyCount={videoHistory.length}
        historyLoading={isLoadingHistory}
        queueCount={queueCount}
        systemStatus={currentVideo?.status === 'processing' ? 'busy' : 'active'}
      />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {isMobile ? (
            <div className="space-y-6">
              <ScenarioInput
                scenarioDescription={scenarioDescription}
                onScenarioChange={setScenarioDescription}
                onGenerateVideo={handleGenerateVideo}
                currentVideo={currentVideo}
                isBusy={isGeneratingVideo}
              />
              <GeneratePanel
                canGeneratePreview={canGeneratePreview}
                canGenerateVideo={canStartGeneration}
                isGeneratingPreview={isGeneratingPreview}
                isGeneratingVideo={isGeneratingVideo}
                previewStatus={previewStatusForPanel}
                previewError={previewError}
                onGeneratePreview={handleGeneratePreview}
                onGenerateVideo={handleGenerateFromState}
              />
              <UnifiedOutputPane
                mode={viewMode}
                onModeChange={setViewMode}
                previewStatus={previewStatusForPane}
                previewImage={previewImageForPane}
                previewPrompt={previewPrompt}
                isGeneratingPreview={isGeneratingPreview}
                onRegeneratePreview={handleGeneratePreview}
                video={currentVideo}
                onRetry={handleRetry}
                settings={settings}
                selectedProvider={provider}
                quality={quality}
                onAspectRatioChange={handleAspectRatioChange}
                history={videoHistory}
                historyLoading={isLoadingHistory}
                onSelectHistory={setCurrentVideo}
                onShowHistory={() => setIsHistoryOpen(true)}
                previewImageData={previewImageForVideo}
              />
              <VideoSettingsPanel
                settings={settings}
                setSettings={setSettings}
                quality={quality}
                setQuality={setQuality}
                selectedProvider={provider}
                onProviderChange={setProvider}
                scenarioDescription={scenarioDescription}
                isGenerating={isGeneratingVideo}
                disabled={currentVideo?.status === 'processing'}
              />
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)_minmax(340px,400px)]">
              <div className="space-y-6">
                <ScenarioInput
                  scenarioDescription={scenarioDescription}
                  onScenarioChange={setScenarioDescription}
                  onGenerateVideo={handleGenerateVideo}
                  currentVideo={currentVideo}
                  isBusy={isGeneratingVideo}
                />
                <GeneratePanel
                  canGeneratePreview={canGeneratePreview}
                  canGenerateVideo={canStartGeneration}
                  isGeneratingPreview={isGeneratingPreview}
                  isGeneratingVideo={isGeneratingVideo}
                  previewStatus={previewStatusForPanel}
                  previewError={previewError}
                  onGeneratePreview={handleGeneratePreview}
                  onGenerateVideo={handleGenerateFromState}
                />
              </div>

              <div className="space-y-6">
                <UnifiedOutputPane
                  mode={viewMode}
                  onModeChange={setViewMode}
                  previewStatus={previewStatusForPane}
                  previewImage={previewImageForPane}
                  previewPrompt={previewPrompt}
                  isGeneratingPreview={isGeneratingPreview}
                  onRegeneratePreview={handleGeneratePreview}
                  video={currentVideo}
                  onRetry={handleRetry}
                  settings={settings}
                  selectedProvider={provider}
                  quality={quality}
                  onAspectRatioChange={handleAspectRatioChange}
                  history={videoHistory}
                  historyLoading={isLoadingHistory}
                  onSelectHistory={setCurrentVideo}
                  onShowHistory={() => setIsHistoryOpen(true)}
                  previewImageData={previewImageForVideo}
                />
              </div>

              <div>
                <VideoSettingsPanel
                  settings={settings}
                  setSettings={setSettings}
                  quality={quality}
                  setQuality={setQuality}
                  selectedProvider={provider}
                  onProviderChange={setProvider}
                  scenarioDescription={scenarioDescription}
                  isGenerating={isGeneratingVideo}
                  disabled={currentVideo?.status === 'processing'}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <HistoryDrawer
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        history={videoHistory}
        isLoading={isLoadingHistory}
        onSelectVideo={setCurrentVideo}
      />
    </div>
  );
}

export default App;
